
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ITask } from '@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes';
import { ActionButton, DropdownOption } from './ProcessWizardTemplate.vue';

export interface StepInfo {
  id: string;
  title: string;
}

export interface ActionButton {
  label: string;
  handler: () => Promise<void> | void;
  variant?: 'primary' | 'secondary' | 'destructive';
  disabled?: boolean | (() => boolean);
  tooltip?: string | (() => string);
}

/** Sidebar yardım videoları (tek video için bile tek öğeli dizi verilir). */
export interface ProcessWizardVideo {
  url: string;
  title?: string;
  description?: string;
  duration?: string;
}

/**
 * ProcessWizardSidebar - Step navigation sidebar with flexible actions
 *
 * Time: O(n) where n = number of steps
 * Space: O(n)
 */
@Component({})
export default class ProcessWizardSidebar extends Vue {
  @Prop({ type: Array, required: true })
  steps!: StepInfo[];

  @Prop({ type: Number, required: true })
  currentStep!: number;

  @Prop({ type: Function, required: true })
  onStepClick!: (step: number) => void;

  @Prop({ type: Function })
  onContinue?: () => Promise<void> | void;

  @Prop({ type: Boolean, default: true })
  canContinue!: boolean;

  @Prop({ type: Object })
  primaryAction?: ActionButton;

  @Prop({ type: Array })
  secondaryActions?: ActionButton[];

  // Optional dedicated Hemen Aç handler (n-1 step)
  @Prop({ type: Function })
  hemenAcHandler?: () => Promise<ITask[] | void> | ITask[] | void;

  @Prop({ type: Object })
  hemenAcAction?: ActionButton;

  @Prop({ type: Array, default: () => [] })
  videos!: ProcessWizardVideo[];

  @Prop({ type: String })
  warningMessage?: string;

  @Prop({ type: String })
  pageName?: string;

  @Prop({ type: Boolean, default: false })
  isProcessing!: boolean;

  @Prop({ type: String })
  processingMessage?: string;


  /**
   * Son adım mı? (Hemen Aç butonu gösterilecek)
   */
  get hemenAc(): boolean {
    // BASIT: 2 step varsa, son step'teyiz demektir
    return this.steps?.length === 2;
  }
  // Primary button label: son adımda "Kuyruğa Aktar", diğerlerinde default
  get primaryButtonLabel(): string {
    const defaultLabel = this.primaryAction?.label || 'Devam Et';
    return this.hemenAc ? 'Kuyruğa Aktar' : defaultLabel;
  }

  getStepStatus(index: number): 'completed' | 'active' | 'upcoming' {
    if (index < this.currentStep - 1) return 'completed';
    if (index === this.currentStep - 1) return 'active';
    return 'upcoming';
  }

  handleStepClick(step: number): void {
    const status = this.getStepStatus(step - 1);
    if (status === 'completed' || status === 'active') {
      this.onStepClick(step);
    }
  }

  async handlePrimaryAction(): Promise<void> {
    if (this.onContinue) {
      await this.onContinue();
    } else if (this.primaryAction?.handler) {
      await this.primaryAction.handler();
    }
  }

  async handleHemenAc(): Promise<void> {
    // console.log('[ProcessWizardSidebar] handleHemenAc called', {
    //   currentStep: this.currentStep,
    //   stepsLength: this.steps?.length,
    //   hasHandler: !!this.hemenAcHandler,
    //   hemenAc: this.hemenAc,
    //   isDisabled: this.isHemenAcDisabled,
    //   hemenAcActionProp: !!this.hemenAcAction
    // });

    // Re-check if handler is available through hemenAcAction
    if (!this.hemenAcHandler && this.hemenAcAction?.handler) {
      // console.log('[ProcessWizardSidebar] Attempting to use handler from hemenAcAction prop');
      this.hemenAcHandler = this.hemenAcAction.handler;
    }

    if (!this.hemenAcHandler) {
      // console.warn('[ProcessWizardSidebar] hemenAcHandler is undefined');
      this.$toast?.warning('Hemen Aç işlemi tanımlı değil');
      return;
    }

    if (!this.hemenAc) {
      // console.warn('[ProcessWizardSidebar] not at n-1 step (hemenAc is false)');
      return;
    }

    // console.log('[ProcessWizardSidebar] Starting Hemen Aç process');
    this.$emit('update:isProcessing', true);
    try {
      await this.hemenAcHandler();
      // console.log('[ProcessWizardSidebar] Hemen Aç completed successfully');
    } catch (error) {
      // console.error('[ProcessWizardSidebar] Hemen aç işlemi hatası:', error);
      this.$toast?.error(`Hemen Aç hatası: ${(error as Error).message}`);
    } finally {
      this.$emit('update:isProcessing', false);
    }
  }

  async handleSecondaryAction(action: ActionButton): Promise<void> {
    if (action.handler) {
      await action.handler();
    }
  }

  async handleDropdownAction(option: DropdownOption): Promise<void> {
    if (!option.handler) {
      console.warn('[ProcessWizardSidebar] No handler for dropdown option');
      return;
    }
    
    // Disabled kontrolü
    if (this.isDropdownOptionDisabled(option)) {
      return;
    }
    
    this.$emit('update:isProcessing', true);
    try {
      await option.handler();
    } catch (error) {
      // console.error('[ProcessWizardSidebar] Dropdown action hatası:', error);
      this.$toast?.error(`İşlem hatası: ${(error as Error).message}`);
    } finally {
      this.$emit('update:isProcessing', false);
    }
  }

  isDropdownOptionDisabled(option: DropdownOption): boolean {
    if (typeof option.disabled === 'function') {
      return option.disabled();
    }
    return option.disabled || false;
  }

  getPrimaryButtonColor(variant?: string): string {
    if (variant === 'secondary') return 'default';
    if (variant === 'destructive') return 'error';
    return 'warning'; // default primary
  }

  get isPrimaryActionDisabled(): boolean {
    if (!this.primaryAction) return false;
    if (typeof this.primaryAction.disabled === 'function') {
      return this.primaryAction.disabled();
    }
    return this.primaryAction.disabled || false;
  }

  isSecondaryActionDisabled(action: ActionButton): boolean {
    if (typeof action.disabled === 'function') {
      return action.disabled();
    }
    return action.disabled || false;
  }

  get hemenAcTooltip(): string {
    if (!this.hemenAcAction || !this.hemenAcAction.tooltip) return '';
    if (typeof this.hemenAcAction.tooltip === 'function') {
      return this.hemenAcAction.tooltip();
    }
    return this.hemenAcAction.tooltip;
  }

  get isHemenAcDisabled(): boolean {
    // ✅ Independent disabled state (primary/secondary buttons should be independent)
    // Force reactivity by accessing steps (which triggers re-computation when wizardSteps changes)
    const _ = this.steps?.length; // Force dependency on steps for reactivity
    const hemenAcAction = this.hemenAcAction;
    
    if (!hemenAcAction) {
      // console.log('[ProcessWizardSidebar] isHemenAcDisabled: no hemenAcAction');
      return false;
    }
    
    if (typeof hemenAcAction.disabled === 'function') {
      try {
        // Call the disabled function - arrow functions preserve 'this' context from where they were defined
        const result = hemenAcAction.disabled();
        // console.log('[ProcessWizardSidebar] isHemenAcDisabled:', result, {
        //   hasDisabledFn: true,
        //   hemenAcAction: !!hemenAcAction,
        //   stepsLength: this.steps?.length
        // });
        return result;
      } catch (e) {
        console.error('[ProcessWizardSidebar] Error calling disabled function:', e, e.stack);
        return false;
      }
    }
    
    const result = hemenAcAction.disabled || false;
    // console.log('[ProcessWizardSidebar] isHemenAcDisabled (non-function):', result);
    return result;
  }

  handleVideoClick(url: string): void {
    window.open(url, '_blank');
  }
}
